/**
 * Investor-docs content loader. (CO 297-003)
 *
 * Renders the five investor-facing documents as pages at /investors/:slug.
 * SOURCE OF TRUTH: comai-plan/docs/investor/*.md — the .md files here are
 * GENERATED copies (see each file's DO-NOT-EDIT header) produced by
 * comai-plan/scripts/sync-investor-docs.mjs. Never edit them here.
 *
 * The section is deliberately unlisted: no nav entry, and the pages set
 * `noindex` — the URL is for direct sharing with investors, not discovery.
 */

const files = import.meta.glob("./investors/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export interface InvestorDoc {
  slug: string;
  title: string;
  /** One-line index description. */
  blurb: string;
  body: string;
}

/** The pitch deck ships as the real PPTX in /public, not a rendered page. */
export const PITCH_DECK = {
  title: "Pitch Deck",
  blurb: "The round in 13 slides — a business that runs at the edge, speaks in prompts, and is composed rather than bought. (PowerPoint, ~15 MB)",
  href: "/investors/ComOS-Pitch-Deck.pptx",
} as const;

/** The same deck as a PDF, for application forms that accept PDF only. */
export const PITCH_DECK_PDF = {
  title: "Pitch Deck (PDF)",
  blurb: "The same deck as a PDF.",
  href: "/investors/ComOS-Pitch-Deck.pdf",
} as const;

/** Display order + titles + index blurbs, keyed by slug. */
const META: Record<string, { title: string; blurb: string }> = {
  "the-business-on-your-desk": {
    title: "The Business on Your Desk",
    blurb:
      "Start here. A merchant's whole business runs on hardware they own, and they operate it by talking to it.",
  },
  "one-pager": {
    title: "One-Pager",
    blurb: "ComOS on a single page: what it is, how it earns, what's live today.",
  },
  faq: {
    title: "Investor FAQ",
    blurb: "The questions investors actually ask, answered on the record.",
  },
  "the-achievement": {
    title: "The Achievement",
    blurb: "The build-proof narrative: what one founder + AI shipped, verified line by line.",
  },
  "replication-pack": {
    title: "Replication Pack",
    blurb: "What it would cost to rebuild this — the cost-to-replicate analysis.",
  },
};

/** Product thesis first; the build proof is the floor under it, not the lead. (CO 556) */
const ORDER = [
  "the-business-on-your-desk",
  "one-pager",
  "faq",
  "the-achievement",
  "replication-pack",
];

const docs: Record<string, InvestorDoc> = {};
for (const [path, body] of Object.entries(files)) {
  const slug = path.split("/").pop()!.replace(/\.md$/, "");
  const meta = META[slug];
  docs[slug] = {
    slug,
    title: meta?.title ?? slug,
    blurb: meta?.blurb ?? "",
    // Strip the generated-file HTML comment header from the rendered body.
    body: body.replace(/^<!--[\s\S]*?-->\s*/, ""),
  };
}

export function getInvestorDoc(slug: string): InvestorDoc | undefined {
  return docs[slug];
}

export const INVESTOR_DOCS: InvestorDoc[] = ORDER.map((s) => docs[s]).filter(
  (d): d is InvestorDoc => Boolean(d),
);
